"use client";

import Image from "next/image";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import BlurFadeText from "@/components/common/blurFadeText";
import SectionEyebrow from "@/components/common/sectionEyebrow";
import { useLenis } from "@/components/ui/lenisContext";

const testimonials = [
  {
    id: "1",
    company: "Associated British Foods",
    logo: "/images/logo/partners/associated-british-foods-logo.svg",
    quote:
      "SML has always provided us with expert impartial and pragmatic analysis, which has helped us to identify our strengths and also highlight areas for future focus.",
    author: "Steve Bradley",
    role: "Group HSE Manager, Associated British Foods PLC",
  },
  {
    id: "2",
    company: "Baxters",
    logo: "/images/logo/partners/baxters.svg",
    quote:
      "SML provided Baxters with interim health and safety support across our European facilities. They were able to provide baseline assessments across the sites along with specific improvement plans. SML were able to mobilise a wide range of experts to understand and tackle specific challenges.",
    author: "Matt Crompton",
    role: "European Manufacturing Director",
  },
  {
    id: "3",
    company: "Clenaware",
    logo: "/images/logo/partners/clenaware.svg",
    quote:
      "SML have consistently provided clear, practical guidance that’s helped improve our safety standards across the business. Their team integrates seamlessly and remains a trusted partner in our ongoing development.",
    author: "Richard Harris",
    role: "Managing Director",
  },
  {
    id: "5",
    company: "Crushed Ice",
    logo: "/images/logo/partners/crushed-ice.svg",
    quote:
      "SML quickly understood our challenges and delivered straightforward solutions that made an immediate impact. Their support has been invaluable in maintaining high standards across our operations.",
    author: "Ryan Stanyer",
    role: "Crushed Ice Ltd",
  },
  {
    id: "6",
    company: "Abbeymill Holmes",
    logo: "/images/logo/partners/abbey-mill.svg",
    quote:
      "We have worked with SML for numerous years. They offer a great service with the expertise needed to offer everything to fully comply in an ever changing world. I would have no reservation about recommending SML to anyone.",
    author: "James Pacifici",
    role: "Abbeymill Homes",
  },
];

function getInitials(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function TestimonialCard({ testimonial, isActive }) {
  return (
    <article
      className={`relative flex h-full min-w-0 min-h-124 flex-col overflow-hidden rounded-lg transition-all duration-500 ${
        isActive ? " bg-white/6" : " bg-white/4"
      }`}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-6 top-4 fzzlex text-[7rem] font-semibold leading-none tracking-[-0.24em] text-white/8 sm:left-7 sm:-top-2 sm:text-[16rem]"
      >
        <span>“</span>
        {}
      </div>

      {}

      <div className="relative flex h-full flex-col p-6 sm:p-7">
        <div className="flex justify-end">
          {testimonial.logo ? (
            <Image
              src={testimonial.logo}
              alt={testimonial.company}
              width={100}
              height={40}
              className="h-8 w-auto max-w-34 object-contain object-right brightness-0 invert opacity-40 sm:h-10"
            />
          ) : (
            <span className="max-w-34 truncate rounded-full bg-white/3 px-3 py-1 font-raleway text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-white/50">
              {testimonial.company}
            </span>
          )}
        </div>

        <p className="mt-10 py-10 font-raleway text-[1.45rem] font-medium leading-[1.14] tracking-[-0.04em] text-white sm:text-[1.58rem] lg:text-[1.5rem]">
          {testimonial.quote}
        </p>

        <div className="mt-auto flex items-center gap-4 border-t border-white/8 pt-6">
          {}

          <div className="space-y-1">
            <p className="font-raleway text-[1.12rem] font-semibold leading-none text-white">
              {testimonial.author}
            </p>
            <p className="max-w-68 text-sm leading-5 text-white/64">
              {testimonial.role}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}

function TestimonialsHeader({ activeIndex, onSelect }) {
  return (
    <div className="grid min-w-0 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(14rem,16rem)_auto] lg:items-center xl:gap-12">
      <div className="min-w-0 space-y-5">
        <SectionEyebrow
          text="Testimonials"
          className="pt-0"
          textClassName="text-white/70"
          amount={0.35}
        />

        <h2 className="max-w-full font-raleway text-[2.75rem] font-semibold leading-[0.95] tracking-[-0.06em] text-white sm:text-[2.9rem] lg:text-[3.25rem]">
          <BlurFadeText
            text="What our clients say"
            delay={0.08}
            amount={0.35}
            blur={11}
            y={20}
          />
        </h2>
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 pt-6 lg:justify-self-end min-w-[120px]">
          {testimonials.map((testimonial, index) => (
            <button
              key={testimonial.id}
              type="button"
              aria-label={`Show testimonial ${index + 1}`}
              aria-pressed={activeIndex === index}
              onClick={() => onSelect(index)}
              className={`relative inline-flex items-center justify-center p-3 rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2`}
            >
              <span
                aria-hidden="true"
                className={`block rounded-full transition-all duration-300 ${
                  activeIndex === index
                    ? "h-2.5 w-7 bg-white"
                    : "h-2.5 w-2.5 bg-white/24 hover:bg-white/38"
                }`}
              />
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-4 pt-6 lg:flex-row lg:items-end lg:justify-self-end lg:gap-10">
          <div className="flex items-end gap-1 font-raleway leading-none">
            <span className="text-[3.8rem] font-semibold tracking-[-0.09em] text-primary sm:text-[4.4rem]">
              4.9
            </span>
            <span className="pb-2 text-[2rem] font-medium text-primary/42 sm:text-[2.2rem]">
              /5
            </span>
          </div>

          <p className="min-w-0 max-w-80 text-sm leading-6 text-white/70 sm:text-[0.98rem]">
            Honest feedback from organisations we support across manufacturing,
            food, property and high-risk operations.
          </p>
        </div>
      </div>
    </div>
  );
}

function DesktopTestimonialsScene() {
  const lenisRef = useLenis();
  const sectionRef = useRef(null);
  const panelRef = useRef(null);
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const [metrics, setMetrics] = useState({
    panelHeight: 0,
    scrollDistance: 0,
    scrollEnd: 1,
    scrollStart: 0,
    stickyTop: 20,
  });
  const [activeIndex, setActiveIndex] = useState(0);

  const { scrollY } = useScroll();

  const rawTrackX = useTransform(
    scrollY,
    [metrics.scrollStart, metrics.scrollEnd],
    [0, -metrics.scrollDistance],
    {
      clamp: true,
    },
  );

  const trackX = useSpring(rawTrackX, {
    stiffness: 88,
    damping: 24,
    mass: 0.85,
  });

  useEffect(() => {
    const measure = () => {
      const section = sectionRef.current;
      const panel = panelRef.current;
      const viewport = viewportRef.current;
      const track = trackRef.current;

      if (!section || !panel || !viewport || !track) {
        return;
      }

      const nextScrollDistance = Math.max(
        0,
        track.scrollWidth - viewport.clientWidth,
      );
      const nextPanelHeight = panel.offsetHeight;
      const nextStickyTop = Math.max(
        24,
        (window.innerHeight - nextPanelHeight) / 2,
      );
      const sectionTop = section.getBoundingClientRect().top + window.scrollY;
      const nextScrollStart = sectionTop - nextStickyTop;
      const nextScrollEnd = nextScrollStart + nextScrollDistance;

      setMetrics((currentMetrics) => {
        const nextMetrics = {
          panelHeight: nextPanelHeight,
          scrollDistance: nextScrollDistance,
          scrollEnd:
            nextScrollEnd <= nextScrollStart
              ? nextScrollStart + 1
              : nextScrollEnd,
          scrollStart: nextScrollStart,
          stickyTop: nextStickyTop,
        };

        return JSON.stringify(currentMetrics) === JSON.stringify(nextMetrics)
          ? currentMetrics
          : nextMetrics;
      });
    };

    measure();
    window.requestAnimationFrame(measure);

    const resizeObserver = new ResizeObserver(() => {
      measure();
    });

    if (viewportRef.current) {
      resizeObserver.observe(viewportRef.current);
    }

    if (panelRef.current) {
      resizeObserver.observe(panelRef.current);
    }

    if (trackRef.current) {
      resizeObserver.observe(trackRef.current);
    }

    window.addEventListener("resize", measure);
    window.addEventListener("load", measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("load", measure);
    };
  }, []);

  useEffect(() => {
    const updateActiveIndex = (currentScroll) => {
      const scrollRange = Math.max(metrics.scrollEnd - metrics.scrollStart, 1);
      const progress = Math.min(
        Math.max((currentScroll - metrics.scrollStart) / scrollRange, 0),
        1,
      );
      const nextIndex = Math.round(progress * (testimonials.length - 1));

      setActiveIndex((currentIndex) =>
        currentIndex === nextIndex ? currentIndex : nextIndex,
      );
    };

    updateActiveIndex(scrollY.get());

    const unsubscribe = scrollY.on("change", (currentScroll) => {
      updateActiveIndex(currentScroll);
    });

    return () => {
      unsubscribe();
    };
  }, [metrics.scrollEnd, metrics.scrollStart, scrollY]);

  const scrollToIndex = (index) => {
    const scrollRange = Math.max(metrics.scrollEnd - metrics.scrollStart, 0);
    const progress =
      testimonials.length > 1 ? index / (testimonials.length - 1) : 0;
    const nextScrollTop = metrics.scrollStart + scrollRange * progress;

    if (lenisRef?.current) {
      lenisRef.current.scrollTo(nextScrollTop, { duration: 1 });
      return;
    }

    window.scrollTo({
      top: nextScrollTop,
      behavior: "smooth",
    });
  };

  const sectionHeight = Math.max(
    metrics.panelHeight + metrics.scrollDistance,
    metrics.panelHeight,
  );

  return (
    <section
      ref={sectionRef}
      className="relative hidden lg:block"
      style={
        sectionHeight > 0
          ? {
              height: `${sectionHeight}px`,
            }
          : undefined
      }
    >
      <div
        ref={panelRef}
        className="sticky"
        style={{ top: `${metrics.stickyTop}px` }}
      >
        <div className="relative overflow-hidden rounded-lg bg-accent py-18 sm:py-20 lg:py-28">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_26%_14%,rgba(255,199,52,0.14),transparent_46%),radial-gradient(circle_at_82%_12%,rgba(255,255,255,0.08),transparent_18%),linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0)_34%)]" />

          <div className="relative mx-auto flex w-full max-w-328 flex-col gap-12 lg:gap-14 mb-20">
            <TestimonialsHeader
              activeIndex={activeIndex}
              onSelect={scrollToIndex}
            />
          </div>
          <div ref={viewportRef} className="relative overflow-hidden">
            {}
            {}

            <motion.div
              ref={trackRef}
              style={{ x: trackX }}
              className="flex items-stretch gap-6 will-change-transform"
            >
              <div aria-hidden="true" className="h-px w-4 shrink-0 xl:w-6" />
              {testimonials.map((testimonial, index) => (
                <div key={testimonial.id} className="w-96 shrink-0 xl:w-116">
                  <TestimonialCard
                    testimonial={testimonial}
                    isActive={activeIndex === index}
                  />
                </div>
              ))}
              <div aria-hidden="true" className="h-px w-20 shrink-0 xl:w-24" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MobileTestimonialsScene() {
  const scrollerRef = useRef(null);
  const cardRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) {
      return undefined;
    }

    let frameId = null;

    const updateActiveIndex = () => {
      frameId = null;

      const anchorPosition = scroller.scrollLeft + scroller.clientWidth * 0.18;
      let nextIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      cardRefs.current.forEach((card, index) => {
        if (!card) {
          return;
        }

        const distance = Math.abs(card.offsetLeft - anchorPosition);

        if (distance < closestDistance) {
          closestDistance = distance;
          nextIndex = index;
        }
      });

      setActiveIndex((currentIndex) =>
        currentIndex === nextIndex ? currentIndex : nextIndex,
      );
    };

    const requestUpdate = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(updateActiveIndex);
    };

    requestUpdate();
    scroller.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      scroller.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
    };
  }, []);

  const scrollToIndex = (index) => {
    const scroller = scrollerRef.current;
    const card = cardRefs.current[index];

    if (!scroller || !card) {
      return;
    }

    scroller.scrollTo({
      left: card.offsetLeft,
      behavior: "smooth",
    });
    setActiveIndex(index);
  };

  return (
    <section className="relative lg:hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_0%,rgba(255,199,52,0.22),transparent_26%),radial-gradient(circle_at_82%_12%,rgba(255,255,255,0.08),transparent_18%),linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0)_34%)]" />

      <div className="relative overflow-hidden rounded-lg bg-[#050607] px-4 py-18 sm:px-6 sm:py-20">
        <div className="relative mx-auto flex w-full max-w-328 flex-col gap-12">
          <TestimonialsHeader
            activeIndex={activeIndex}
            onSelect={scrollToIndex}
          />

          <div
            ref={scrollerRef}
            className="testimonial-scroller flex min-w-0 snap-x snap-mandatory gap-4 overflow-x-auto pb-1 sm:gap-5"
          >
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                ref={(node) => {
                  cardRefs.current[index] = node;
                }}
                className="w-full max-w-100 shrink-0 snap-start sm:w-100"
              >
                <TestimonialCard
                  testimonial={testimonial}
                  isActive={activeIndex === index}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .testimonial-scroller {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .testimonial-scroller::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}

export default function TestimonialsSection() {
  return (
    <div className="w-full">
      <DesktopTestimonialsScene />
      <MobileTestimonialsScene />
    </div>
  );
}
